import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  ArrowLeftIcon,
  UserIcon,
  TagIcon,
  HeartIcon,
  ArrowTrendingUpIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  StarIcon,
  LinkIcon,
  CodeBracketIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import ReviewsContainer from '../reviews/ReviewsContainer';
import { UsePromptHandler } from './UsePromptHandler';
import { copyToClipboard } from '../../lib/clipboard';
import { api, Tag, RatingAggregateDto, RatingDto, AttachmentDto, PromptQualityAnalysis } from '../../services/api';
import { AuthSessionExpiredError } from '../../services/authService';
import { loginRequest } from '../../config/authConfig';
import { AttachmentList } from './AttachmentList';
import { QualityReviewModal } from './QualityReviewModal';
import { NeedsImprovementBadge, useAutoApproveThreshold } from './NeedsImprovementBadge';
import { getUserInitials, formatDateShort, getCategoryBadgeColor } from '../../utils/userUtils';

export interface PromptDetail {
  id: number;
  title: string;
  promptText: string;
  description?: string;
  category: {
    id: number;
    name: string;
  };
  tags: Array<{
    id: number;
    name: string;
  }>;
  submittedBy: string;
  userId?: string;
  isOwner?: boolean;
  userDisplayName?: string;
  userEmail?: string;
  createdAt: string;
  updatedAt: string;
  qualityScore?: number | null;
  metrics: {
    rating: number;
    totalRatings: number;
    uses: number;
    likes: number;
  };
}

interface PromptDetailViewProps {
  promptId: number;
  onBack?: () => void;
  onDelete?: () => void;
  userId?: string;
}

export const PromptDetailView: React.FC<PromptDetailViewProps> = ({
  promptId,
  onBack,
  onDelete,
  userId
}) => {
  // Authentication hooks
  const { instance: msalInstance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const currentUserAccount = accounts[0];
  
  const [prompt, setPrompt] = useState<PromptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [usesCount, setUsesCount] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'content' | 'comments' | 'analytics'>('content');
  
  // isOwner is computed server-side (API compares access token claim against stored userId)
  // to avoid cross-token pairwise sub mismatch between ID token and access token in Azure AD.
  const isOwner = !!prompt?.isOwner;
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Tag editing state
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editedTags, setEditedTags] = useState<Array<{ id: number; name: string }>>([]);
  const [newTagName, setNewTagName] = useState('');
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Rating state
  const [ratingAggregate, setRatingAggregate] = useState<RatingAggregateDto | null>(null);
  const [userRatingId, setUserRatingId] = useState<number | null>(null);
  const [userRatingScore, setUserRatingScore] = useState<number | null>(null);
  const [hoverScore, setHoverScore] = useState<number | null>(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [showAuthRatingMessage, setShowAuthRatingMessage] = useState(false);

  // Full prompt edit dialog state
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editPromptText, setEditPromptText] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategoryId, setEditCategoryId] = useState<number>(0);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  // FR-7.2 quality analysis state for the edit flow
  const [editQualityAnalysis, setEditQualityAnalysis] = useState<PromptQualityAnalysis | null>(null);
  const [editAnalysisFailed, setEditAnalysisFailed] = useState(false);
  const autoApproveThreshold = useAutoApproveThreshold();
  // Guards against an orphaned save when the user navigates away mid-analysis
  const isMountedRef = React.useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Attachment state
  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchPrompt = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await api.request<Record<string, any>>(`/prompts/${promptId}`);
        console.log('Prompt detail API response:', data);
        
        // Transform the API response to match our PromptDetail interface
        const transformedPrompt: PromptDetail = {
          id: data.id,
          title: data.title,
          promptText: data.promptText,
          description: data.description,
          category: data.category,
          tags: data.tags || [],
          submittedBy: data.submittedBy,
          userId: data.userId,
          isOwner: data.isOwner === true,
          userDisplayName: data.userDisplayName,
          userEmail: data.userEmail,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          qualityScore: data.qualityScore ?? null,
          metrics: {
            rating: data.rating || 0,
            totalRatings: data.ratingCount || 0,
            uses: data.uses || 0,
            likes: data.likes || 0
          }
        };
        
        setPrompt(transformedPrompt);
        setEditedTags([...transformedPrompt.tags]);
        setLikesCount(transformedPrompt.metrics.likes);
        setUsesCount(transformedPrompt.metrics.uses);

        // Set liked state from API response (isLikedByCurrentUser is true only for authenticated users)
        setIsLiked(data.isLikedByCurrentUser === true);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to load prompt');
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [promptId, userId]);

  // Separate useEffect for view tracking - only runs once when promptId changes
  useEffect(() => {
    const trackView = async () => {
      try {
        // For now, just log the view tracking - the endpoint may not exist yet
        console.log('Tracking view for prompt:', promptId);
        // await fetch(`${API_BASE_URL}/prompts/${promptId}/view`, { method: 'POST' });
      } catch (error) {
        console.error('Failed to track view:', error);
      }
    };

    trackView();
  }, [promptId]); // Only depends on promptId, not userId

  // Fetch rating aggregate (and user's rating if authenticated)
  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const aggregate = await api.getRatingAggregate(promptId);
        setRatingAggregate(aggregate);
      } catch (e) {
        console.error('Failed to fetch rating aggregate:', e);
      }
      if (isAuthenticated) {
        try {
          const userRating = await api.getUserRating(promptId);
          if (userRating) {
            setUserRatingId(userRating.id);
            setUserRatingScore(userRating.score);
          }
        } catch (e) {
          console.error('Failed to fetch user rating:', e);
        }
      }
    };
    fetchRatings();
  }, [promptId, isAuthenticated]);

  // Load attachments on mount
  useEffect(() => {
    api.getAttachments(promptId)
      .then(setAttachments)
      .catch(() => { /* non-fatal — attachments section simply stays empty */ });
  }, [promptId]);

  const BLOCKED_EXTENSIONS = ['.exe', '.bat', '.sh', '.ps1', '.cmd', '.msi', '.dmg', '.app'];
  const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB

  const validateAttachmentFile = (file: File): string | null => {
    const lower = file.name.toLowerCase();
    if (BLOCKED_EXTENSIONS.some(ext => lower.endsWith(ext))) {
      return `File type not allowed: "${file.name}". Executable files are blocked.`;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return `File too large: "${file.name}" (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is 10 MB.`;
    }
    if (file.size === 0) {
      return `File is empty: "${file.name}".`;
    }
    return null;
  };

  const handleAttachmentFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const err = validateAttachmentFile(file);
    if (err) {
      toast.error(err);
      return;
    }

    setIsUploadingAttachment(true);
    const toastId = toast.loading(`Uploading ${file.name}…`);
    try {
      const dto = await api.uploadAttachment(promptId, file);
      setAttachments(prev => [...prev, dto]);
      toast.success(`${file.name} attached`, { id: toastId });
    } catch (err2: any) {
      toast.error(err2?.message || 'Upload failed', { id: toastId });
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleAttachmentDeleted = (attachmentId: number) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleRatingClick = async (score: number) => {
    if (!isAuthenticated || isSubmittingRating) return;
    setIsSubmittingRating(true);
    try {
      let result: RatingDto;
      if (userRatingId) {
        result = await api.updateRating(userRatingId, score);
      } else {
        const localAccountId = currentUserAccount?.localAccountId ?? '';
        result = await api.submitRating(promptId, score, localAccountId);
        setUserRatingId(result.id);
      }
      setUserRatingScore(result.score);
      // Refresh aggregate
      const aggregate = await api.getRatingAggregate(promptId);
      setRatingAggregate(aggregate);
    } catch (e) {
      console.error('Failed to submit rating:', e);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      alert('You must be logged in to like prompts');
      return;
    }

    const previousIsLiked = isLiked;
    const previousLikesCount = likesCount;

    // Optimistic update
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);

    try {
      const updatedPrompt = isLiked
        ? await api.unlikePrompt(promptId)
        : await api.likePrompt(promptId);
      setLikesCount(updatedPrompt.likes ?? likesCount);
    } catch (error) {
      console.error('Failed to update like status:', error);
      // Revert optimistic update on error
      setIsLiked(previousIsLiked);
      setLikesCount(previousLikesCount);
    }
  };

  const handleUsageTracked = () => {
    setUsesCount(prev => prev + 1);
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/prompts/${promptId}`;
    const result = await copyToClipboard(url);
    if (result.success) {
      toast.success('Link copied!');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const handleOpenInVSCode = async () => {
    if (!isAuthenticated || !prompt) return;

    // Increment use counter once (before any branch)
    try {
      await api.incrementUses(promptId);
      setUsesCount(prev => prev + 1);
    } catch (e) {
      console.error('Failed to track usage:', e);
    }

    // Copy to clipboard as fallback backup
    await copyToClipboard(prompt.promptText);

    // Detect if VS Code opens by watching for page visibility loss
    let vsCodeOpened = false;
    const handleVisibilityChange = () => {
      if (document.hidden) vsCodeOpened = true;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Navigate to vscode:// URI — browser stays on page if VS Code handles it;
    // if not installed, browser shows protocol-not-found dialog and page stays too.
    window.location.href = 'vscode://';

    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (vsCodeOpened) {
        toast.success('Opening VS Code — prompt copied to clipboard, paste into your new file.');
      } else {
        toast.warning("VS Code didn't open — prompt copied to clipboard instead.");
      }
    }, 1500);
  };

  const handleEditPrompt = async () => {
    if (!prompt) return;
    setEditTitle(prompt.title);
    setEditPromptText(prompt.promptText);
    setEditDescription(prompt.description || '');
    setEditCategoryId(prompt.category.id);
    setEditAnalysisFailed(false);
    setEditQualityAnalysis(null);
    setIsEditingPrompt(true);
    if (categories.length === 0) {
      setLoadingCategories(true);
      try {
        const cats = await api.getCategories();
        setCategories(cats);
      } catch (e) {
        console.error('Failed to load categories', e);
      } finally {
        setLoadingCategories(false);
      }
    }
  };

  const performUpdate = async (
    content: { title: string; promptText: string; description: string; categoryId: number },
    analysisId: string,
    qualityScore: number
  ) => {
    if (!prompt) return;
    try {
      const tagNames = prompt.tags.map(t => t.name);
      const updatedPrompt = await api.updatePrompt(prompt.id, {
        ...content,
        tags: tagNames,
        analysisId
      });
      if (updatedPrompt) {
        const selectedCategory = categories.find(c => c.id === content.categoryId);
        setPrompt(prev => prev ? {
          ...prev,
          title: updatedPrompt.title,
          promptText: updatedPrompt.promptText,
          description: content.description,
          category: selectedCategory
            ? { id: selectedCategory.id, name: selectedCategory.name }
            : prev.category,
          updatedAt: updatedPrompt.updatedAt,
          qualityScore
        } : prev);
        setIsEditingPrompt(false);
        toast.success(`Saved — Quality Score: ${qualityScore}/10`);
      }
    } catch (err) {
      console.error('Failed to save prompt:', err);
      alert('Failed to save changes. Please try again.');
    }
  };

  // FR-7.2: edits run the quality analysis before saving, same flow as create.
  const handleSavePrompt = async () => {
    if (!prompt) return;
    if (!editTitle.trim() || !editPromptText.trim()) {
      alert('Title and Prompt Text are required.');
      return;
    }
    setIsSavingPrompt(true);
    setEditAnalysisFailed(false);
    const content = {
      title: editTitle.trim(),
      promptText: editPromptText.trim(),
      description: editDescription.trim(),
      categoryId: editCategoryId
    };
    let analysis: PromptQualityAnalysis | null = null;
    try {
      analysis = await api.analyzePrompt({
        title: content.title,
        promptText: content.promptText,
        categoryId: content.categoryId,
        tags: prompt.tags.map(t => t.name)
      });
    } catch (err: any) {
      console.error('Quality analysis failed:', err);
      if (isMountedRef.current) {
        setEditAnalysisFailed(true);
        setIsSavingPrompt(false);
        if (err instanceof AuthSessionExpiredError) {
          toast.error('Your session has expired', {
            description: 'Please sign in again to continue.',
            action: {
              label: 'Sign In Again',
              onClick: () => msalInstance.loginRedirect(loginRequest),
            },
          });
        } else {
          toast.error('Quality analysis unavailable. Please try again.', {
            description: err?.message || undefined
          });
        }
      }
      return;
    }
    if (!analysis || !isMountedRef.current) return; // navigated away mid-analysis — no orphaned save
    try {
      if (analysis.qualityScore >= autoApproveThreshold) {
        await performUpdate(content, analysis.analysisId, analysis.qualityScore);
      } else {
        setEditQualityAnalysis(analysis);
      }
    } finally {
      if (isMountedRef.current) setIsSavingPrompt(false);
    }
  };

  const handleEditAcceptChanges = async () => {
    if (!prompt || !editQualityAnalysis?.suggestedRewrite) return;
    const content = {
      title: editTitle.trim(),
      promptText: editQualityAnalysis.suggestedRewrite,
      description: editDescription.trim(),
      categoryId: editCategoryId
    };
    setIsSavingPrompt(true);
    let rewriteAnalysis: PromptQualityAnalysis | null = null;
    try {
      // The save endpoint verifies the analysisId's content hash against the submitted
      // content, so the rewrite needs its own analysis before it can be saved.
      rewriteAnalysis = await api.analyzePrompt({
        title: content.title,
        promptText: content.promptText,
        categoryId: content.categoryId,
        tags: prompt.tags.map(t => t.name)
      });
    } catch (err: any) {
      console.error('Quality analysis failed for suggested rewrite:', err);
      // Keep the modal open so the user can retry Accept or fall back to Edit & Resubmit
      if (isMountedRef.current) {
        setIsSavingPrompt(false);
        if (err instanceof AuthSessionExpiredError) {
          toast.error('Your session has expired', {
            description: 'Please sign in again to continue.',
            action: {
              label: 'Sign In Again',
              onClick: () => msalInstance.loginRedirect(loginRequest),
            },
          });
        } else {
          toast.error('Quality analysis unavailable. Please try again.', {
            description: err?.message || undefined
          });
        }
      }
      return;
    }
    if (!rewriteAnalysis || !isMountedRef.current) return;
    try {
      setEditQualityAnalysis(null);
      setEditPromptText(content.promptText);
      await performUpdate(content, rewriteAnalysis.analysisId, rewriteAnalysis.qualityScore);
    } finally {
      if (isMountedRef.current) setIsSavingPrompt(false);
    }
  };

  const handleEditResubmit = () => {
    // Edit dialog keeps the original (unmodified) content; next save re-analyzes
    setEditQualityAnalysis(null);
  };

  const handleEditTags = async () => {
    setIsEditingTags(true);
    setEditedTags([...prompt?.tags || []]);
    await fetchAvailableTags();
  };

  const fetchAvailableTags = async () => {
    setLoadingTags(true);
    try {
      const tags = await api.getTags();
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to fetch available tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleCancelEditTags = () => {
    setIsEditingTags(false);
    setEditedTags([...prompt?.tags || []]);
    setNewTagName('');
  };

  const handleAddTag = () => {
    const trimmedName = newTagName.trim();
    if (trimmedName && !editedTags.some(tag => tag.name.toLowerCase() === trimmedName.toLowerCase())) {
      // Create a temporary ID for new tags (negative to distinguish from real IDs)
      const tempId = -Date.now();
      setEditedTags(prev => [...prev, { id: tempId, name: trimmedName }]);
      setNewTagName('');
    }
  };

  const handleRemoveTag = (tagId: number) => {
    setEditedTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  const handleSelectExistingTag = (tag: Tag) => {
    if (!editedTags.some(editedTag => editedTag.id === tag.id)) {
      setEditedTags(prev => [...prev, { id: tag.id, name: tag.name }]);
    }
  };

  const handleSaveTags = async () => {
    if (!prompt) return;

    setIsSavingTags(true);
    try {
      const tagNames = editedTags.map(tag => tag.name);
      
      // Use the existing updatePrompt API endpoint
      const updatedPrompt = await api.updatePrompt(prompt.id, {
        title: prompt.title,
        promptText: prompt.promptText,
        description: prompt.description || '',
        categoryId: prompt.category.id,
        tags: tagNames
      });

      // Update the local state with the response
      if (updatedPrompt) {
        const transformedPrompt: PromptDetail = {
          ...prompt,
          tags: updatedPrompt.tags.map(tag => ({ id: tag.id, name: tag.name })),
          updatedAt: updatedPrompt.updatedAt
        };
        
        setPrompt(transformedPrompt);
        setEditedTags([...transformedPrompt.tags]);
        setIsEditingTags(false);
      }
    } catch (error) {
      console.error('Failed to update tags:', error);
      alert('Failed to update tags. Please try again.');
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleDeletePrompt = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.deletePrompt(promptId);
      setShowDeleteConfirm(false);
      if (onDelete) onDelete();
      else if (onBack) onBack();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete prompt');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading prompt...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <div className="text-red-600 mb-4">
              <h3 className="text-lg font-medium">Error Loading Prompt</h3>
              <p>{error}</p>
            </div>
            <div className="space-x-2">
              <Button onClick={() => window.location.reload()}>Try Again</Button>
              {onBack && (
                <Button variant="outline" onClick={onBack}>Go Back</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <h3 className="text-lg font-medium mb-2">Prompt Not Found</h3>
            <p className="text-gray-600 mb-4">The prompt you're looking for doesn't exist or has been removed.</p>
            {onBack && (
              <Button onClick={onBack}>Go Back</Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Callback to handle review count updates from ReviewsContainer
  const handleReviewCountUpdate = (count: number) => {
    setReviewsCount(count);
  };

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-slate-600 text-white p-6">
        <div className="flex items-center gap-4 mb-4">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{prompt.title}</h1>
              <NeedsImprovementBadge qualityScore={prompt.qualityScore} />
            </div>
            <p className="text-slate-300 mt-1">Detailed prompt view and reviews</p>
          </div>
          
          {/* Copy Link button - visible to all authenticated users */}
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={handleCopyLink}
            aria-label="Copy link to this prompt"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Copy Link
          </Button>

          {/* Edit/Delete buttons - only show for prompt owner */}
          {isOwner && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="bg-white text-slate-600 border-white hover:bg-white/80 hover:text-slate-800"
                onClick={handleEditPrompt}
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Prompt
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-6 text-sm">
          {prompt?.metrics && (
            <>
              <button
                onClick={handleLike}
                disabled={!isAuthenticated}
                className={`flex items-center gap-1 hover:bg-white/10 rounded px-2 py-1 transition-colors ${
                  !isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                title={!isAuthenticated ? 'You must be logged in to like prompts' : isLiked ? 'Unlike this prompt' : 'Like this prompt'}
              >
                {isLiked ? (
                  <HeartIconSolid className="w-4 h-4 text-red-500" />
                ) : (
                  <HeartIcon className="w-4 h-4" />
                )}
                <span>{formatNumber(likesCount)} likes</span>
              </button>
              <div className="flex items-center gap-1">
                <ArrowTrendingUpIcon className="w-4 h-4" />
                <span>{formatNumber(usesCount)} uses</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Tab Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Tab Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab Navigation */}
            <div className="mb-6">
              <nav className="flex gap-4 w-full">
                <button
                  onClick={() => setActiveTab('content')}
                  className={`py-3 px-4 rounded-full font-semibold text-base transition-all duration-200 border flex-1 ${
                    activeTab === 'content'
                      ? 'bg-blue-100 text-blue-700 border-blue-200 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-3 justify-center">
                    <TagIcon className="w-5 h-5" />
                    Content
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('comments')}
                  className={`py-3 px-4 rounded-full font-semibold text-base transition-all duration-200 border flex-1 ${
                    activeTab === 'comments'
                      ? 'bg-blue-100 text-blue-700 border-blue-200 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-3 justify-center">
                    <ChatBubbleLeftRightIcon className="w-5 h-5" />
                    Comments
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`py-3 px-4 rounded-full font-semibold text-base transition-all duration-200 border flex-1 ${
                    activeTab === 'analytics'
                      ? 'bg-blue-100 text-blue-700 border-blue-200 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-3 justify-center">
                    <ChartBarIcon className="w-5 h-5" />
                    Analytics
                  </div>
                </button>
              </nav>
            </div>
            {activeTab === 'content' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Prompt Content</CardTitle>
                    <div className="flex items-center gap-2">
                      {isAuthenticated && (
                        <Button
                          variant="outline"
                          onClick={handleOpenInVSCode}
                          aria-label="Open prompt in VS Code"
                        >
                          <CodeBracketIcon className="w-4 h-4 mr-2" />
                          Open in VS Code
                        </Button>
                      )}
                      <UsePromptHandler
                        promptId={prompt.id}
                        promptTitle={prompt.title}
                        promptText={prompt.promptText}
                        onUsageTracked={handleUsageTracked}
                      >
                        {({ onUsePrompt }) => (
                          <Button onClick={onUsePrompt}>
                            Use This Prompt
                          </Button>
                        )}
                      </UsePromptHandler>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {prompt.description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">Description</h4>
                      <p className="text-sm text-gray-700">{prompt.description}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap">
                    {prompt.promptText}
                  </div>
                  <AttachmentList
                    promptId={promptId}
                    attachments={attachments}
                    isOwner={isOwner}
                    isAuthenticated={isAuthenticated}
                    onAttachmentDeleted={handleAttachmentDeleted}
                  />
                </CardContent>
              </Card>
            )}

            {activeTab === 'comments' && (
              <ReviewsContainer
                promptId={prompt.id}
                promptTitle={prompt.title}
                isAuthenticated={isAuthenticated}
                reviewerName={currentUserAccount?.name || currentUserAccount?.username}
                onReviewCountChange={handleReviewCountUpdate}
              />
            )}

            {activeTab === 'analytics' && (
              <Card>
                <CardHeader>
                  <CardTitle>Usage Analytics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-900">Uses</span>
                      </div>
                      <div className="text-2xl font-bold text-green-900 mt-2">{formatNumber(usesCount)}</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <HeartIcon className="w-5 h-5 text-red-600" />
                        <span className="text-sm font-medium text-red-900">Likes</span>
                      </div>
                      <div className="text-2xl font-bold text-red-900 mt-2">{formatNumber(likesCount)}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-600" />
                        <span className="text-sm font-medium text-purple-900">Reviews</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-900 mt-2">{formatNumber(reviewsCount)}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium">Performance Overview</h4>
                    <p className="text-sm text-gray-600">
                      This prompt has been used {formatNumber(usesCount)} times, 
                      with {formatNumber(likesCount)} users showing their appreciation through likes.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Author Card */}
            <Card>
              <CardHeader className="bg-gray-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-gray-600" />
                  Author
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  {/* Avatar with initials */}
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                      {getUserInitials(prompt.userDisplayName || prompt.submittedBy)}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Author information - left aligned */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-base">
                      {prompt.userDisplayName || prompt.submittedBy}
                    </h3>
                    <p className="text-sm text-gray-600">Prompt Author</p>
                    {prompt.userEmail && prompt.userEmail !== 'unknown@email.com' && (
                      <p className="text-sm text-gray-500">{prompt.userEmail}</p>
                    )}
                  </div>
                </div>
                
                {/* View Profile button */}
                <Button variant="outline" className="w-full" size="sm">
                  View Profile
                </Button>
              </CardContent>
            </Card>

            {/* Rating Card */}
            <Card>
              <CardHeader className="bg-gray-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <StarIcon className="w-5 h-5 text-gray-600" />
                  Rating
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Aggregate display */}
                <div className="flex items-center gap-2">
                  {ratingAggregate && ratingAggregate.totalRatings > 0 ? (
                    <>
                      <span className="text-2xl font-bold text-gray-900">
                        {ratingAggregate.averageRating?.toFixed(1)}
                      </span>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(star => {
                          const avg = ratingAggregate.averageRating ?? 0;
                          return avg >= star ? (
                            <StarIconSolid key={star} className="w-4 h-4 text-yellow-400" />
                          ) : (
                            <StarIcon key={star} className="w-4 h-4 text-gray-300" />
                          );
                        })}
                      </div>
                      <span className="text-sm text-gray-500">
                        ({ratingAggregate.totalRatings})
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500 italic">No ratings yet</span>
                  )}
                </div>

                {/* Interactive stars for all users */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {isAuthenticated && userRatingScore ? 'Your rating:' : 'Rate this prompt:'}
                  </p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(star => {
                      const displayScore = hoverScore ?? (isAuthenticated ? (userRatingScore ?? 0) : 0);
                      const filled = displayScore >= star;
                      return (
                        <button
                          key={star}
                          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                          disabled={isAuthenticated && isSubmittingRating}
                          onClick={() => {
                            if (!isAuthenticated) {
                              setShowAuthRatingMessage(true);
                              setTimeout(() => setShowAuthRatingMessage(false), 2500);
                              return;
                            }
                            handleRatingClick(star);
                          }}
                          onMouseEnter={() => setHoverScore(star)}
                          onMouseLeave={() => setHoverScore(null)}
                          className="focus:outline-none disabled:opacity-50"
                          title={isAuthenticated ? `Rate ${star} star${star > 1 ? 's' : ''}` : 'Sign in to rate this prompt'}
                        >
                          {filled ? (
                            <StarIconSolid className="w-6 h-6 text-yellow-400 hover:scale-110 transition-transform" />
                          ) : (
                            <StarIcon className="w-6 h-6 text-gray-300 hover:text-yellow-400 hover:scale-110 transition-transform" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {!isAuthenticated && (
                    <p className={`text-xs mt-1 ${showAuthRatingMessage ? 'text-yellow-600 font-medium' : 'text-gray-400 italic'}`}>
                      Sign in to rate this prompt
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Details Card */}
            <Card>
              <CardHeader className="bg-gray-50 rounded-t-lg flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <TagIcon className="w-5 h-5 text-gray-600" />
                  Details
                </CardTitle>
                {isOwner && !isEditingTags && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditTags}
                    className="h-8 px-2 text-xs"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category */}
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Category</h4>
                  <Badge 
                    className={`${getCategoryBadgeColor(prompt.category.name)} border font-medium`}
                  >
                    {prompt.category.name}
                  </Badge>
                </div>

                {/* Tags */}
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Tags</h4>
                  {!isEditingTags ? (
                    <div className="flex flex-wrap gap-2">
                      {prompt.tags.length > 0 ? (
                        prompt.tags.map(tag => (
                          <span 
                            key={tag.id} 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tag.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500 italic">No tags</span>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Existing tags */}
                      <div className="flex flex-wrap gap-2">
                        {editedTags.map(tag => (
                          <div key={tag.id} className="flex items-center gap-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {tag.name}
                            </span>
                            <button
                              onClick={() => handleRemoveTag(tag.id)}
                              className="text-red-500 hover:text-red-700 ml-1"
                              title="Remove tag"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Loading indicator for tags */}
                      {loadingTags && (
                        <div className="text-xs text-gray-500 mb-2">
                          Loading available tags...
                        </div>
                      )}

                      {/* Select existing tag */}
                      {!loadingTags && availableTags.length > 0 && (
                        <div className="mb-2">
                          <select
                            onChange={(e) => {
                              const selectedTag = availableTags.find(tag => tag.id === parseInt(e.target.value));
                              if (selectedTag) {
                                handleSelectExistingTag(selectedTag);
                                e.target.value = '';
                              }
                            }}
                            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-500"
                            defaultValue=""
                          >
                            <option value="" disabled>Select existing tag...</option>
                            {availableTags
                              .filter(tag => !editedTags.some(editedTag => editedTag.id === tag.id))
                              .map(tag => (
                                <option key={tag.id} value={tag.id}>
                                  {tag.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* Add new tag */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                          placeholder="Add new tag..."
                          className="flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-500"
                          maxLength={50}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddTag}
                          disabled={!newTagName.trim()}
                          className="h-6 px-2"
                        >
                          <PlusIcon className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleSaveTags}
                          disabled={isSavingTags}
                          className="h-6 px-3 text-xs"
                        >
                          {isSavingTags ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckIcon className="w-3 h-3 mr-1" />
                              Save
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEditTags}
                          disabled={isSavingTags}
                          className="h-6 px-3 text-xs"
                        >
                          <XMarkIcon className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Horizontal line after Tags */}
                <hr className="border-gray-200" />

                {/* Dates with left-right alignment */}
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-600">Created:</h4>
                    <p className="text-gray-900">{formatDateShort(prompt.createdAt)}</p>
                  </div>
                  {prompt.updatedAt !== prompt.createdAt && (
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-600">Updated:</h4>
                      <p className="text-gray-900">{formatDateShort(prompt.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit prompt dialog */}
      <Dialog open={isEditingPrompt} onOpenChange={(open) => { if (!isSavingPrompt) { setIsEditingPrompt(open); if (!open) setEditAnalysisFailed(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilIcon className="w-5 h-5" />
              Edit Prompt
            </DialogTitle>
            <DialogDescription>
              Update the title, content, description, and category of this prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={200}
                aria-invalid={!editTitle.trim() || undefined}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500${!editTitle.trim() ? ' border-red-500' : ''}`}
                placeholder="Prompt title"
              />
              {!editTitle.trim() && (
                <p className="mt-1 text-xs text-red-600">Title is required.</p>
              )}
            </div>
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={500}
                rows={2}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Short description (optional)"
              />
            </div>
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              {loadingCategories ? (
                <div className="text-sm text-gray-500">Loading categories...</div>
              ) : (
                <select
                  value={editCategoryId}
                  onChange={(e) => setEditCategoryId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Prompt Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prompt Text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={editPromptText}
                onChange={(e) => setEditPromptText(e.target.value)}
                maxLength={50000}
                rows={10}
                className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                placeholder="The full prompt text"
              />
              <p className="text-xs text-gray-500 mt-1">{editPromptText.length}/50000</p>
            </div>
            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
              <AttachmentList
                promptId={promptId}
                attachments={attachments}
                isOwner={isOwner}
                isAuthenticated={isAuthenticated}
                onAttachmentDeleted={handleAttachmentDeleted}
              />
              <input
                ref={attachmentInputRef}
                type="file"
                className="hidden"
                onChange={handleAttachmentFileChange}
                aria-label="Upload attachment"
              />
              <button
                type="button"
                onClick={() => attachmentInputRef.current?.click()}
                disabled={isUploadingAttachment}
                className="mt-2 flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50 w-full justify-center"
              >
                <PaperClipIcon className="w-4 h-4" aria-hidden="true" />
                {isUploadingAttachment ? 'Uploading…' : 'Attach a file (max 10 MB)'}
              </button>
            </div>
          </div>
          {/* FR-7.2 failure path: analysis is required before save — no bypass */}
          {editAnalysisFailed && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3"
            >
              <p className="text-sm text-red-700">Quality analysis unavailable. Please try again.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSavePrompt}
                disabled={isSavingPrompt}
              >
                Retry
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditingPrompt(false)}
              disabled={isSavingPrompt}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePrompt}
              disabled={isSavingPrompt || editAnalysisFailed || !editTitle.trim() || !editPromptText.trim()}
            >
              {isSavingPrompt ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FR-7.2 comparison modal — shown when the edit's analysis scores below the threshold */}
      <QualityReviewModal
        open={!!editQualityAnalysis}
        analysis={editQualityAnalysis}
        originalText={editPromptText.trim()}
        isSaving={isSavingPrompt}
        onAcceptChanges={handleEditAcceptChanges}
        onEditResubmit={handleEditResubmit}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { if (!isDeleting) setShowDeleteConfirm(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <TrashIcon className="w-5 h-5" />
              Delete Prompt
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>"{prompt?.title}"</strong>?
              <span className="block text-red-600 mt-1">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{deleteError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeletePrompt}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Prompt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromptDetailView;